import { createContext, useContext } from "react";

/*
 * Auth context and hook, split from the provider so react-refresh can
 * fast-refresh the provider (see context/theme.js for the same reasoning).
 *
 * The session is a JWT in localStorage under the key "session" — exactly what the
 * Next.js app stored. NextAuth was configured there but never used: no page
 * imported useSession or signIn, and all thirteen call sites read localStorage
 * directly. Dropping the dependency changes no behaviour.
 */

export const AuthContext = createContext(null);

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}

/** Where a role lands after signing in. Mirrors the Next.js login page. */
export function landingRouteFor(role) {
    return role === "superAdmin" ? "/super-admin" : "/dashboard";
}

/*
 * Role capabilities, in one place.
 *
 * The backend enforces these with requireRole() — this is only about what the UI
 * offers, so a manager is not shown buttons that would 403. Never treat it as
 * the security boundary.
 */
const CAPABILITIES = {
    superAdmin: ["platform", "manageUsers", "manageOrg", "viewAll"],
    admin: ["manageUsers", "manageOrg", "viewAll"],
    manager: ["viewAll"],
    employee: [],
};

export function roleCan(role, capability) {
    return (CAPABILITIES[role] ?? []).includes(capability);
}
